import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import isAdministrator from "../utilities/checkPrivileges";
import organizationSettingsService from "../services/organizationSettingsService";
import { expiryBadgeClass, expiryLabel, formatDateTime } from "../utils/tokenExpiry";

const readFileText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.readAsText(file);
  });

const readFileBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buf = reader.result;
      if (!(buf instanceof ArrayBuffer)) {
        reject(new Error("Datei konnte nicht gelesen werden"));
        return;
      }
      const bytes = new Uint8Array(buf);
      let binary = "";
      bytes.forEach((b) => {
        binary += String.fromCharCode(b);
      });
      resolve(window.btoa(binary));
    };
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.readAsArrayBuffer(file);
  });

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const downloadBase64 = (b64, filename, mime = "application/octet-stream") => {
  const binary = window.atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  downloadBlob(new Blob([bytes], { type: mime }), filename);
};

const StatusBadge = ({ status, label }) => (
  <span className={`badge ${expiryBadgeClass(status)}`}>{label}</span>
);

const Settings = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const admin = isAdministrator();

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const [certFile, setCertFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [combinedPem, setCombinedPem] = useState("");
  const [apnsPreview, setApnsPreview] = useState(null);
  const [apnsBusy, setApnsBusy] = useState(false);

  const [vendorCertFile, setVendorCertFile] = useState(null);
  const [vendorKeyFile, setVendorKeyFile] = useState(null);
  const [vendorKeyPassword, setVendorKeyPassword] = useState("");
  const [vendorBusy, setVendorBusy] = useState(false);
  const [csrEmail, setCsrEmail] = useState("");
  const [csrCountry, setCsrCountry] = useState("DE");
  const [pushCsrCn, setPushCsrCn] = useState("Xavier MDM Push");
  const [vendorCsrCn, setVendorCsrCn] = useState("Xavier MDM Vendor");

  const [depCn, setDepCn] = useState("");
  const [depDays, setDepDays] = useState(365);
  const [p7mFile, setP7mFile] = useState(null);
  const [depForce, setDepForce] = useState(false);
  const [depBusy, setDepBusy] = useState(false);

  const [vppFile, setVppFile] = useState(null);
  const [vppText, setVppText] = useState("");
  const [vppBusy, setVppBusy] = useState(false);

  const [signCertFile, setSignCertFile] = useState(null);
  const [signKeyFile, setSignKeyFile] = useState(null);
  const [signCombined, setSignCombined] = useState("");
  const [signPreview, setSignPreview] = useState(null);
  const [signBusy, setSignBusy] = useState(false);
  const [signCn, setSignCn] = useState("Xavier Profile Signing");
  const [signOrg, setSignOrg] = useState("Xavier MDM");
  const [signDays, setSignDays] = useState(1095);

  const [supervisionBusy, setSupervisionBusy] = useState(false);
  const [supervisionCn, setSupervisionCn] = useState("Xavier Supervision Identity");
  const [supervisionOrg, setSupervisionOrg] = useState("Xavier MDM");
  const [supervisionDays, setSupervisionDays] = useState(1825);
  const [supervisionP12Password, setSupervisionP12Password] = useState("");
  const [configOrg, setConfigOrg] = useState({
    name: "",
    address: "",
    email: "",
    phone: "",
    appleId: "",
    alternateDirectoryServicesId: "",
    uuid: "",
  });

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await organizationSettingsService.getSettings();
      setSettings(data);
      setDepCn((prev) => (prev || data?.dep?.name || prev));
      const csr = data?.apns?.csr;
      if (csr?.email) setCsrEmail(csr.email);
      if (csr?.country) setCsrCountry(csr.country);
      if (csr?.vendorCommonName) setVendorCsrCn(csr.vendorCommonName);
      if (csr?.pushCommonName) setPushCsrCn(csr.pushCommonName);
      const org = data?.configuratorOrganization;
      if (org) {
        setConfigOrg({
          name: org.name || data?.dep?.orgName || data?.vpp?.orgName || "",
          address: org.address || "",
          email: org.email || "",
          phone: org.phone || "",
          appleId: org.appleId || "",
          alternateDirectoryServicesId: org.alternateDirectoryServicesId || "",
          uuid: org.uuid || "",
        });
        if (org.name) setSupervisionOrg((prev) => (prev === "Xavier MDM" ? org.name : prev));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Einstellungen konnten nicht geladen werden.");
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && admin) loadSettings();
  }, [user, admin, loadSettings]);

  const collectApnsPem = async () => {
    let certificatePem = combinedPem;
    let privateKeyPem = "";
    if (certFile) certificatePem = `${certificatePem}\n${await readFileText(certFile)}`;
    if (keyFile) privateKeyPem = await readFileText(keyFile);
    return { certificatePem, privateKeyPem };
  };

  const handleApnsPreview = async (e) => {
    e.preventDefault();
    setApnsBusy(true);
    try {
      const payload = await collectApnsPem();
      const data = await organizationSettingsService.previewApns(payload);
      setApnsPreview(data);
      toast.success("Zertifikat geprüft.");
    } catch (err) {
      setApnsPreview(null);
      toast.error(err.response?.data?.message || err.message || "Prüfung fehlgeschlagen.");
    } finally {
      setApnsBusy(false);
    }
  };

  const handleApnsRenew = async () => {
    if (!window.confirm("Push-Zertifikat jetzt in NanoHUB speichern? Falsches Topic unterbricht die Gerätekommunikation.")) {
      return;
    }
    setApnsBusy(true);
    try {
      const payload = await collectApnsPem();
      const data = await organizationSettingsService.renewApns(payload);
      setApnsPreview(data);
      toast.success(
        data.usedStoredPushKey
          ? "Push-Zertifikat wurde mit dem hier erzeugten Schlüssel in NanoHUB gespeichert."
          : "Push-Zertifikat wurde erneuert."
      );
      setCertFile(null);
      setKeyFile(null);
      setCombinedPem("");
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Erneuerung fehlgeschlagen.");
    } finally {
      setApnsBusy(false);
    }
  };

  const handleVendorSave = async (e) => {
    e.preventDefault();
    if (!vendorCertFile) {
      toast.error("Bitte die mdm.cer aus dem Apple Developer Portal wählen.");
      return;
    }
    setVendorBusy(true);
    try {
      const certificateBase64 = await readFileBase64(vendorCertFile);
      let privateKeyPem = "";
      if (vendorKeyFile) privateKeyPem = await readFileText(vendorKeyFile);
      await organizationSettingsService.saveApnsVendor({
        certificateBase64,
        privateKeyPem,
        privateKeyPassword: vendorKeyPassword || undefined,
      });
      toast.success("MDM-Vendor-Zertifikat hinterlegt. Als Nächstes den Vendor-Signed CSR laden.");
      setVendorCertFile(null);
      setVendorKeyFile(null);
      setVendorKeyPassword("");
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Vendor-Zertifikat konnte nicht gespeichert werden.");
    } finally {
      setVendorBusy(false);
    }
  };

  const handleVendorCsrDownload = async () => {
    if (!csrEmail.trim()) {
      toast.error("Bitte eine E-Mail für den CSR-Subject angeben.");
      return;
    }
    const ok = window.confirm(
      "Xavier erzeugt ein neues Vendor-Schlüsselpaar und eine VendorCertificateRequest.csr. " +
      "Die CSR im Apple Developer Portal unter Certificates → Services → MDM CSR hochladen, " +
      "mdm.cer herunterladen und hier hinterlegen. Das bisherige Vendor-Zertifikat bleibt nutzbar, " +
      "bis die neue mdm.cer gespeichert ist. Fortfahren?"
    );
    if (!ok) return;
    setVendorBusy(true);
    try {
      const data = await organizationSettingsService.downloadApnsVendorCsr({
        email: csrEmail.trim() || undefined,
        country: csrCountry.trim() || undefined,
        commonName: vendorCsrCn.trim() || undefined,
      });
      downloadBase64(data.contentBase64, data.filename || "VendorCertificateRequest.csr", data.contentType);
      toast.success("Vendor-CSR geladen. Jetzt im Developer Portal als MDM CSR hochladen und die mdm.cer hier speichern.");
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Vendor-CSR konnte nicht erzeugt werden.");
    } finally {
      setVendorBusy(false);
    }
  };

  const handleVendorSignedCsrDownload = async () => {
    if (!csrEmail.trim()) {
      toast.error("Bitte eine E-Mail für den CSR-Subject angeben.");
      return;
    }
    const ok = window.confirm(
      "Xavier erzeugt ein neues Push-Schlüsselpaar und denselben Vendor-Signed CSR wie " +
      "mdmctl mdmcert vendor -sign (PushCertificateRequest.plist). Den privaten Schlüssel behält nur Xavier. " +
      "Unter identity.apple.com/pushcert unbedingt Renew am bestehenden Zertifikat wählen — " +
      "nicht Create, sonst ändert sich das Topic. Fortfahren?"
    );
    if (!ok) return;
    setVendorBusy(true);
    try {
      const data = await organizationSettingsService.downloadVendorSignedCsr({
        email: csrEmail.trim() || undefined,
        country: csrCountry.trim() || undefined,
        commonName: pushCsrCn.trim() || undefined,
      });
      downloadBase64(
        data.contentBase64,
        data.filename || "PushCertificateRequest.plist",
        data.contentType || "application/octet-stream"
      );
      toast.success(
        "PushCertificateRequest.plist geladen (Base64 wie mdmctl). Diese Datei bei Apple unter Renew hochladen — sie beginnt mit PD94, nicht mit <?xml."
      );
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Vendor-Signed CSR konnte nicht erzeugt werden.");
    } finally {
      setVendorBusy(false);
    }
  };

  const handleDepCertDownload = async () => {
    const ok = window.confirm(
      "Achtung: Das erzeugt ein neues Schlüsselpaar. Danach lassen sich nur noch Tokens entschlüsseln, " +
      "die Apple mit genau diesem neuen PEM verschlüsselt. Für die jährliche Token-Erneuerung " +
      "dieses Zertifikat nicht neu erzeugen — sonst bricht die Kette. Wirklich fortfahren?"
    );
    if (!ok) return;
    setDepBusy(true);
    try {
      const response = await organizationSettingsService.downloadDepPublicCert({
        cn: depCn.trim() || undefined,
        validityDays: depDays || undefined,
      });
      const filename =
        response.headers?.["content-disposition"]?.match(/filename="?([^"]+)"?/i)?.[1] ||
        "dep-public.pem";
      downloadBlob(response.data, filename);
      toast.success("Neues Staging-Zertifikat gespeichert. Jetzt dieses PEM in ABM/ASM hochladen, danach ein frisches Token (.p7m) herunterladen und hier importieren.");
    } catch (err) {
      toast.error(err.response?.data?.message || "DEP-Zertifikat konnte nicht geladen werden.");
    } finally {
      setDepBusy(false);
    }
  };

  const handleDepRenew = async (e) => {
    e.preventDefault();
    if (!p7mFile) {
      toast.error("Bitte die von Apple heruntergeladene .p7m-Datei wählen.");
      return;
    }
    setDepBusy(true);
    try {
      const p7mBase64 = await readFileBase64(p7mFile);
      const data = await organizationSettingsService.renewDepToken({
        p7mBase64,
        force: depForce,
      });
      toast.success(
        data.accessTokenExpiry
          ? `DEP-Token importiert. Gültig bis ${formatDateTime(data.accessTokenExpiry)}.`
          : "DEP-Token importiert."
      );
      setP7mFile(null);
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "DEP-Token konnte nicht importiert werden.");
    } finally {
      setDepBusy(false);
    }
  };

  const handleVppRenew = async (e) => {
    e.preventDefault();
    setVppBusy(true);
    try {
      let tokenText = vppText;
      if (vppFile) {
        tokenText = await readFileText(vppFile);
      }
      if (!String(tokenText || "").trim()) {
        toast.error("Bitte eine Inhalte-Token-Datei (.vpptoken) wählen oder den Token-Text einfügen.");
        return;
      }
      const data = await organizationSettingsService.renewVppToken({ tokenText });
      toast.success(
        data.tokenExpiry
          ? `Inhalte-Token gespeichert. Gültig bis ${formatDateTime(data.tokenExpiry)}.`
          : "Inhalte-Token gespeichert."
      );
      setVppFile(null);
      setVppText("");
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Inhalte-Token konnte nicht importiert werden.");
    } finally {
      setVppBusy(false);
    }
  };

  if (!user) return null;

  if (!admin) {
    return (
      <div className="container">
        <h2 className="mb-3">Einstellungen</h2>
        <div className="alert alert-warning">
          Organisations-Tokens dürfen nur von Administratoren eingesehen und erneuert werden.
        </div>
      </div>
    );
  }

  if (loading && !settings) {
    return <p className="text-center my-4">Laden...</p>;
  }

  const apns = settings?.apns || {};
  const vendor = apns.vendor || {};
  const pendingPush = apns.pendingPushRequest;
  const dep = settings?.dep || {};
  const vpp = settings?.vpp || {};
  const profileSigning = settings?.profileSigning || {};
  const supervisionIdentity = settings?.supervisionIdentity || {};

  const collectSignPem = async () => {
    let certificatePem = signCombined;
    let privateKeyPem = "";
    if (signCertFile) certificatePem = `${certificatePem}\n${await readFileText(signCertFile)}`;
    if (signKeyFile) privateKeyPem = await readFileText(signKeyFile);
    return { certificatePem, privateKeyPem };
  };

  const handleSignPreview = async (e) => {
    e.preventDefault();
    setSignBusy(true);
    try {
      const data = await organizationSettingsService.previewProfileSigning(await collectSignPem());
      setSignPreview(data);
      toast.success("Signaturzertifikat geprüft.");
    } catch (err) {
      setSignPreview(null);
      toast.error(err.response?.data?.message || err.message || "Prüfung fehlgeschlagen.");
    } finally {
      setSignBusy(false);
    }
  };

  const handleSignSave = async (e) => {
    e.preventDefault();
    setSignBusy(true);
    try {
      await organizationSettingsService.saveProfileSigning(await collectSignPem());
      toast.success("Signaturzertifikat gespeichert. Neue InstallProfile-Befehle werden damit signiert.");
      setSignCertFile(null);
      setSignKeyFile(null);
      setSignCombined("");
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSignBusy(false);
    }
  };

  const handleSignGenerate = async () => {
    const ok = window.confirm(
      "Xavier erzeugt ein selbstsigniertes Organisationszertifikat und speichert den privaten Schlüssel nur hier. " +
      "Ein vorhandenes Signaturzertifikat wird ersetzt. Geräte müssen dem öffentlichen Zertifikat vertrauen " +
      "(Stammzertifikat-Profil), sonst gilt die Signatur als nicht vertrauenswürdig. Fortfahren?"
    );
    if (!ok) return;
    setSignBusy(true);
    try {
      await organizationSettingsService.generateProfileSigning({
        commonName: signCn.trim() || undefined,
        organization: signOrg.trim() || undefined,
        validityDays: signDays || undefined,
      });
      toast.success("Signaturzertifikat erzeugt. Als Nächstes das öffentliche Zertifikat auf die Geräte bringen.");
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Erzeugen fehlgeschlagen.");
    } finally {
      setSignBusy(false);
    }
  };

  const handleSignPublicDownload = async () => {
    setSignBusy(true);
    try {
      const response = await organizationSettingsService.downloadProfileSigningPublicCert();
      const filename =
        response.headers?.["content-disposition"]?.match(/filename="?([^"]+)"?/i)?.[1] ||
        "xavier-profile-signing.pem";
      downloadBlob(response.data, filename);
      toast.success("Öffentliches Zertifikat geladen (ohne privaten Schlüssel).");
    } catch (err) {
      toast.error(err.response?.data?.message || "Download fehlgeschlagen.");
    } finally {
      setSignBusy(false);
    }
  };

  const handleSignTrustProfile = async () => {
    setSignBusy(true);
    try {
      const created = await organizationSettingsService.createSigningTrustProfile();
      toast.success(`Vertrauensprofil „${created.PayloadDisplayName}“ ist in der Bibliothek. Gruppen zuweisen, damit Geräte der Signatur vertrauen.`);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Vertrauensprofil fehlgeschlagen.");
    } finally {
      setSignBusy(false);
    }
  };

  const handleSupervisionGenerate = async () => {
    const ok = window.confirm(
      "Xavier erzeugt eine Betreuungsidentität (Supervision Identity) und speichert den privaten Schlüssel verschlüsselt. " +
      "Eine vorhandene Identität wird ersetzt. Für Configurator-Paarung ohne Gerätefreigabe muss dieselbe Identität " +
      "bei der Betreuung verwendet worden sein. Fortfahren?"
    );
    if (!ok) return;
    setSupervisionBusy(true);
    try {
      await organizationSettingsService.generateSupervisionIdentity({
        commonName: supervisionCn.trim() || undefined,
        organization: supervisionOrg.trim() || undefined,
        validityDays: supervisionDays || undefined,
      });
      toast.success("Betreuungsidentität erzeugt. Als .p12 für Apple Configurator herunterladen.");
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Erzeugen fehlgeschlagen.");
    } finally {
      setSupervisionBusy(false);
    }
  };

  const setConfigOrgField = (key, value) => setConfigOrg((prev) => ({ ...prev, [key]: value }));

  const handleConfiguratorOrgSave = async () => {
    if (!configOrg.name.trim()) {
      toast.error("Organisationsname ist erforderlich.");
      return;
    }
    setSupervisionBusy(true);
    try {
      const saved = await organizationSettingsService.saveConfiguratorOrganization({
        name: configOrg.name.trim(),
        address: configOrg.address.trim(),
        email: configOrg.email.trim(),
        phone: configOrg.phone.trim(),
        appleId: configOrg.appleId.trim(),
        alternateDirectoryServicesId: configOrg.alternateDirectoryServicesId.trim(),
      });
      setConfigOrg((prev) => ({ ...prev, ...saved }));
      toast.success("Organisationsdaten gespeichert.");
      await loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSupervisionBusy(false);
    }
  };

  const downloadSupervisionBlobError = async (err, fallback) => {
    let message = fallback;
    try {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        const parsed = JSON.parse(text);
        if (parsed?.message) message = parsed.message;
      } else if (err.response?.data?.message) {
        message = err.response.data.message;
      }
    } catch {
      /* keep default */
    }
    return message;
  };

  const handleSupervisionP12Download = async () => {
    const password = supervisionP12Password.trim();
    if (password.length < 4) {
      toast.error("Bitte ein Passwort mit mindestens 4 Zeichen für das .p12 setzen.");
      return;
    }
    setSupervisionBusy(true);
    try {
      const response = await organizationSettingsService.downloadSupervisionIdentityP12({ password });
      const filename =
        response.headers?.["content-disposition"]?.match(/filename="?([^"]+)"?/i)?.[1] ||
        "xavier-supervision-identity.p12";
      downloadBlob(response.data, filename);
      toast.success("Betreuungsidentität (.p12) geladen.");
    } catch (err) {
      toast.error(await downloadSupervisionBlobError(err, "Download fehlgeschlagen."));
    } finally {
      setSupervisionBusy(false);
    }
  };

  const handleOrganizationFileDownload = async () => {
    if (!configOrg.name.trim()) {
      toast.error("Bitte zuerst Organisationsdaten speichern (Name erforderlich).");
      return;
    }
    const password = supervisionP12Password.trim();
    if (password.length < 4) {
      toast.error("Bitte ein Passwort mit mindestens 4 Zeichen für die .organization setzen.");
      return;
    }
    setSupervisionBusy(true);
    try {
      if (!configOrg.uuid) {
        await organizationSettingsService.saveConfiguratorOrganization({
          name: configOrg.name.trim(),
          address: configOrg.address.trim(),
          email: configOrg.email.trim(),
          phone: configOrg.phone.trim(),
          appleId: configOrg.appleId.trim(),
          alternateDirectoryServicesId: configOrg.alternateDirectoryServicesId.trim(),
        });
      }
      const response = await organizationSettingsService.downloadConfiguratorOrganization({ password });
      const filename =
        response.headers?.["content-disposition"]?.match(/filename="?([^"]+)"?/i)?.[1] ||
        "xavier.organization";
      downloadBlob(response.data, filename);
      toast.success(".organization geladen — in Apple Configurator unter Organisation importieren.");
      await loadSettings();
    } catch (err) {
      toast.error(await downloadSupervisionBlobError(err, "Download fehlgeschlagen."));
    } finally {
      setSupervisionBusy(false);
    }
  };

  return (
    <div className="container">
      <h2 className="mb-2">Einstellungen</h2>
      <p className="text-muted mb-4">
        Organisationsweite Tokens für Apple-Push (APNs), Geräte-Registrierung (DEP) und Apps and Books (VPP).
        Diese Seite ist Administratoren vorbehalten, damit spätere Standort-Benutzer nur ihre Geräte sehen und verwalten können.
      </p>

      <div className="card mb-4 p-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h3 className="h5 mb-1">APNs Push-Zertifikat</h3>
            <p className="text-muted small mb-2">
              Jährliche Erneuerung wie in der NanoMDM-/MicroMDM-Doku: Vendor-Signed CSR erzeugen,
              unter <a href="https://identity.apple.com/pushcert/" target="_blank" rel="noreferrer">identity.apple.com/pushcert</a> mit
              <strong> Renew</strong> hochladen, das PEM danach in NanoHUB speichern
              (<code>PUT /api/v1/nanomdm/pushcert</code>). Das ersetzt <code>mdmctl mdmcert vendor -sign</code> und
              <code>mdmctl mdmcert upload</code>.
            </p>
          </div>
          <StatusBadge
            status={apns.status}
            label={expiryLabel(apns.notAfter, apns.daysRemaining)}
          />
        </div>
        <div className="d-flex flex-wrap gap-3 small mb-3">
          <span><strong>Topic:</strong> <code>{apns.topic || "unbekannt"}</code></span>
          {apns.notAfter && (
            <span><strong>Gültig bis:</strong> {formatDateTime(apns.notAfter)}</span>
          )}
          {apns.updatedAt && (
            <span><strong>Zuletzt erneuert:</strong> {formatDateTime(apns.updatedAt)}</span>
          )}
        </div>
        {apns.error && <div className="alert alert-warning py-2 small">{apns.error}</div>}

        <h4 className="h6 mt-2">1. MDM-Vendor-Zertifikat (Developer Portal)</h4>
        <p className="text-muted small">
          Die <code>mdm.cer</code> (Typ <strong>MDM CSR</strong>) liegt in Xavier, nicht in NanoMDM:
          Zertifikat und Schlüssel in MongoDB (<code>OrganizationSettings</code>), der Schlüssel verschlüsselt.
          Ohne gültiges Vendor-Zertifikat kann Xavier den Signed CSR nicht erzeugen.
          Apple stellt diese Datei jedes Jahr neu aus — hier ersetzen, das aktive Push-Zertifikat in NanoHUB bleibt unberührt.
        </p>
        <div className="d-flex flex-wrap gap-3 small mb-3 align-items-center">
          <StatusBadge
            status={vendor.status}
            label={vendor.configured ? expiryLabel(vendor.notAfter, vendor.daysRemaining) : "kein Vendor-Zertifikat"}
          />
          {vendor.subject && <span><strong>Vendor:</strong> {vendor.subject}</span>}
          {vendor.issuer && <span><strong>Aussteller:</strong> {vendor.issuer}</span>}
          {vendor.notAfter && <span><strong>Vendor gültig bis:</strong> {formatDateTime(vendor.notAfter)}</span>}
          {vendor.updatedAt && <span><strong>Zuletzt hinterlegt:</strong> {formatDateTime(vendor.updatedAt)}</span>}
        </div>
        {vendor.configured && vendor.status === "expired" && (
          <div className="alert alert-danger py-2 small">
            Das Vendor-Zertifikat ist abgelaufen. Zuerst unten einen Vendor-CSR laden, im Developer Portal ein neues
            MDM-CSR-Zertifikat ausstellen und die neue <code>mdm.cer</code> hier aktualisieren.
          </div>
        )}
        {vendor.pendingCsr && (
          <div className="alert alert-info py-2 small">
            Es liegt ein Vendor-CSR bereit. Die heruntergeladene <code>mdm.cer</code> hier speichern — der Schlüssel ist schon hinterlegt.
          </div>
        )}

        <form onSubmit={handleVendorSave} className="mb-3">
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label">mdm.cer (Developer Portal)</label>
              <input
                type="file"
                className="form-control"
                accept=".cer,.pem,.crt,.der"
                onChange={(e) => setVendorCertFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">
                Vendor-Privatschlüssel {vendor.pendingCsr || vendor.configured ? "(optional, falls schon hinterlegt)" : ""}
              </label>
              <input
                type="file"
                className="form-control"
                accept=".key,.pem,.txt"
                onChange={(e) => setVendorKeyFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Schlüssel-Passwort (falls verschlüsselt)</label>
              <input
                type="password"
                className="form-control"
                autoComplete="off"
                value={vendorKeyPassword}
                onChange={(e) => setVendorKeyPassword(e.target.value)}
                placeholder="mdmctl -password"
              />
            </div>
          </div>
          <button type="submit" className="btn btn-outline-secondary mt-3" disabled={vendorBusy}>
            {vendorBusy ? "Speichere…" : vendor.configured ? "Vendor-Zertifikat aktualisieren" : "Vendor-Zertifikat hinterlegen"}
          </button>
        </form>

        <p className="small mb-2">
          Jährliche Vendor-Erneuerung: Xavier erzeugt <code>VendorCertificateRequest.csr</code> wie
          <code> mdmctl mdmcert vendor</code>. Im Developer Portal unter Certificates → Services →
          <strong> MDM CSR</strong> ein <em>neues</em> Zertifikat ausstellen (dort gibt es kein Renew),
          <code>mdm.cer</code> herunterladen und oben aktualisieren.
          E-Mail, Land und CN sind nur CSR-Beschriftung — fürs Push-Topic zählt ausschließlich
          <strong> Renew</strong> mit derselben Apple-ID, nicht diese Felder.
        </p>
        <div className="row g-2 align-items-end mb-4">
          <div className="col-md-4">
            <label className="form-label">E-Mail (CSR-Subject, nur Beschriftung)</label>
            <input
              type="email"
              className="form-control"
              value={csrEmail}
              onChange={(e) => setCsrEmail(e.target.value)}
              placeholder="admin@example.org"
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Land</label>
            <input
              type="text"
              className="form-control"
              maxLength={2}
              value={csrCountry}
              onChange={(e) => setCsrCountry(e.target.value.toUpperCase())}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">Vendor-CN</label>
            <input
              type="text"
              className="form-control"
              value={vendorCsrCn}
              onChange={(e) => setVendorCsrCn(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              disabled={vendorBusy}
              onClick={handleVendorCsrDownload}
            >
              Vendor-CSR laden
            </button>
          </div>
        </div>

        <h4 className="h6">2. Vendor-Signed CSR laden</h4>
        <p className="text-muted small">
          Xavier erzeugt ein neues Push-Schlüsselpaar und dieselbe Datei wie
          <code> mdmctl mdmcert vendor -sign</code>: eine Base64-Zeile, darin die XML-Plist.
          Unter{" "}
          <a href="https://identity.apple.com/pushcert/" target="_blank" rel="noreferrer">identity.apple.com/pushcert</a>{" "}
          mit derselben Apple-ID <strong>Renew</strong> am bestehenden Push-Zertifikat wählen — nicht Create,
          sonst ändert sich das Topic und alle Geräte verlieren den Push.
        </p>
        {pendingPush?.createdAt && (
          <div className="alert alert-secondary py-2 small">
            Offener Push-CSR vom {formatDateTime(pendingPush.createdAt)}
            {pendingPush.commonName ? ` (${pendingPush.commonName})` : ""}.
            Das PEM von Apple unten speichern — den privaten Schlüssel hat Xavier bereits.
          </div>
        )}
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-6">
            <label className="form-label">Push-CN</label>
            <input
              type="text"
              className="form-control"
              value={pushCsrCn}
              onChange={(e) => setPushCsrCn(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <button
              type="button"
              className="btn btn-primary w-100"
              disabled={vendorBusy || !vendor.configured || vendor.status === "expired"}
              onClick={handleVendorSignedCsrDownload}
            >
              {vendorBusy ? "Erzeuge…" : "Vendor-Signed CSR laden"}
            </button>
          </div>
        </div>
        {!vendor.configured && (
          <p className="small text-warning mb-3">Zuerst Schritt 1: gültige <code>mdm.cer</code> hinterlegen.</p>
        )}

        <h4 className="h6">3. PEM von Apple in NanoHUB speichern</h4>
        <p className="text-muted small">
          Das von identity.apple.com heruntergeladene Zertifikat prüfen und speichern.
          {pendingPush
            ? " Der passende private Schlüssel liegt bereits in Xavier (aus Schritt 2)."
            : " Ohne zuvor erzeugten CSR Zertifikat und unverschlüsselten privaten Schlüssel zusammen hochladen."}
        </p>
        <form onSubmit={handleApnsPreview}>
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label">Zertifikat (PEM von Apple)</label>
              <input
                type="file"
                className="form-control"
                accept=".pem,.cer,.crt,.txt"
                onChange={(e) => setCertFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">
                Privater Schlüssel {pendingPush ? "(optional, schon hinterlegt)" : "(unverschlüsselt, PEM)"}
              </label>
              <input
                type="file"
                className="form-control"
                accept=".pem,.key,.txt"
                onChange={(e) => setKeyFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Oder beides als ein PEM-Text</label>
              <textarea
                className="form-control font-monospace"
                rows={4}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----BEGIN PRIVATE KEY-----"
                value={combinedPem}
                onChange={(e) => setCombinedPem(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-3 d-flex flex-wrap gap-2">
            <button type="submit" className="btn btn-outline-primary" disabled={apnsBusy}>
              {apnsBusy ? "Prüfe…" : "Zertifikat prüfen"}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={apnsBusy || !apnsPreview}
              onClick={handleApnsRenew}
            >
              In NanoHUB speichern
            </button>
          </div>
        </form>

        {apnsPreview && (
          <div className="mt-3 p-3 bg-light rounded">
            <div className="small">
              <div><strong>Topic:</strong> <code>{apnsPreview.topic}</code></div>
              <div><strong>Gültig von:</strong> {formatDateTime(apnsPreview.notBefore)}</div>
              <div><strong>Gültig bis:</strong> {formatDateTime(apnsPreview.notAfter)}</div>
              <div><strong>Aussteller:</strong> {apnsPreview.issuer}</div>
              {apnsPreview.usedStoredPushKey && (
                <div className="text-success">Passender Schlüssel aus dem Vendor-Signed CSR wird verwendet.</div>
              )}
            </div>
            {apnsPreview.warnings?.length > 0 && (
              <ul className="mt-2 mb-0 text-warning small">
                {apnsPreview.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="card mb-4 p-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h3 className="h5 mb-1">DEP-Token (Geräte-Registrierung)</h3>
            <p className="text-muted small mb-2">
              Jährliche Erneuerung: in ABM/ASM nur das Token neu herunterladen und die <code>.p7m</code> hier importieren.
              Das öffentliche Zertifikat am MDM-Server bleibt. Ein neues PEM würde die Kette brechen.
            </p>
          </div>
          <StatusBadge
            status={dep.status}
            label={expiryLabel(dep.accessTokenExpiry, dep.daysRemaining)}
          />
        </div>
        <div className="d-flex flex-wrap gap-3 small mb-3">
          <span><strong>DEP-Name:</strong> {dep.name || "—"}</span>
          {dep.orgName && <span><strong>Organisation:</strong> {dep.orgName}</span>}
          {dep.serverName && <span><strong>Server:</strong> {dep.serverName}</span>}
          {dep.accessTokenExpiry && (
            <span><strong>Token gültig bis:</strong> {formatDateTime(dep.accessTokenExpiry)}</span>
          )}
          {dep.consumerKeyHint && (
            <span><strong>Consumer-Key:</strong> <code>{dep.consumerKeyHint}</code></span>
          )}
        </div>
        {dep.error && <div className="alert alert-warning py-2 small">{dep.error}</div>}

        <form onSubmit={handleDepRenew}>
          <div className="row g-2 align-items-end">
            <div className="col-md-6">
              <label className="form-label">Apple-Token (.p7m)</label>
              <input
                type="file"
                className="form-control"
                accept=".p7m,.txt,.bin"
                onChange={(e) => setP7mFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-md-6">
              <div className="form-check mt-4">
                <input
                  id="depForce"
                  type="checkbox"
                  className="form-check-input"
                  checked={depForce}
                  onChange={(e) => setDepForce(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="depForce">
                  Consumer-Key-Prüfung überschreiben (<code>force=1</code>)
                </label>
              </div>
            </div>
          </div>
          <button type="submit" className="btn btn-primary mt-3" disabled={depBusy}>
            {depBusy ? "Importiere…" : "DEP-Token importieren"}
          </button>
        </form>

        <details className="mt-4">
          <summary className="small text-muted">Nur Erst-Einrichtung: neues öffentliches Zertifikat</summary>
          <p className="small text-warning mt-2 mb-2">
            Erzeugt ein neues Schlüsselpaar. Danach müssen das PEM in ABM und ein damit erzeugtes Token zusammenpassen.
            Für die jährliche Erneuerung nicht verwenden.
          </p>
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label">Common Name</label>
              <input
                type="text"
                className="form-control"
                value={depCn}
                onChange={(e) => setDepCn(e.target.value)}
                placeholder="DEP-Name"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Gültigkeit (Tage)</label>
              <input
                type="number"
                min={1}
                max={3650}
                className="form-control"
                value={depDays}
                onChange={(e) => setDepDays(Number(e.target.value))}
              />
            </div>
            <div className="col-md-4">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={depBusy}
                onClick={handleDepCertDownload}
              >
                Öffentliches Zertifikat laden
              </button>
            </div>
          </div>
        </details>
      </div>

      <div className="card mb-4 p-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h3 className="h5 mb-1">Profil-Signaturzertifikat</h3>
            <p className="text-muted small mb-2">
              Eigenes Organisationszertifikat zum Signieren von <code>.mobileconfig</code>.
              <strong> Nicht</strong> das APNs-Push-Zertifikat. Ablauf wird wie APNs/DEP in den
              Einstellungen und unter Geräte-Registrierung angezeigt.
            </p>
          </div>
          <StatusBadge
            status={profileSigning.status}
            label={profileSigning.configured ? expiryLabel(profileSigning.notAfter, profileSigning.daysRemaining) : "kein Zertifikat"}
          />
        </div>
        <div className="d-flex flex-wrap gap-3 small mb-3">
          {profileSigning.subject && <span><strong>Subject:</strong> {profileSigning.subject}</span>}
          {profileSigning.issuer && <span><strong>Aussteller:</strong> {profileSigning.issuer}</span>}
          {profileSigning.notAfter && <span><strong>Gültig bis:</strong> {formatDateTime(profileSigning.notAfter)}</span>}
          {profileSigning.updatedAt && <span><strong>Zuletzt gesetzt:</strong> {formatDateTime(profileSigning.updatedAt)}</span>}
        </div>
        <div className="alert alert-secondary small">
          Geräte müssen dem <em>öffentlichen</em> Zertifikat vertrauen, sonst erscheint die Signatur als
          ungültig. Xavier kann ein selbstsigniertes CA-Zertifikat erzeugen; den privaten Schlüssel
          behält nur Xavier. Alternativ PEM + Schlüssel einer internen CA hochladen.
        </div>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-4">
            <label className="form-label">Common Name</label>
            <input className="form-control" value={signCn} onChange={(e) => setSignCn(e.target.value)} />
          </div>
          <div className="col-md-4">
            <label className="form-label">Organisation</label>
            <input className="form-control" value={signOrg} onChange={(e) => setSignOrg(e.target.value)} />
          </div>
          <div className="col-md-2">
            <label className="form-label">Gültigkeit (Tage)</label>
            <input
              type="number"
              min={30}
              max={3650}
              className="form-control"
              value={signDays}
              onChange={(e) => setSignDays(Number(e.target.value))}
            />
          </div>
          <div className="col-md-2">
            <button type="button" className="btn btn-primary w-100" disabled={signBusy} onClick={handleSignGenerate}>
              {signBusy ? "…" : "Erzeugen"}
            </button>
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={signBusy || !profileSigning.configured} onClick={handleSignPublicDownload}>
            Öffentliches Zertifikat laden
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" disabled={signBusy || !profileSigning.configured} onClick={handleSignTrustProfile}>
            Vertrauensprofil in Bibliothek
          </button>
          {profileSigning.configured && (
            <Link to="/profiles" className="btn btn-link btn-sm">Zur Profil-Bibliothek</Link>
          )}
        </div>
        {signPreview?.warnings?.length > 0 && (
          <div className="alert alert-warning py-2 small">
            {signPreview.warnings.map((item) => <div key={item}>{item}</div>)}
          </div>
        )}
        <details className="mb-2">
          <summary className="small text-muted">Vorhandenes Zertifikat hochladen (interne CA)</summary>
        <form className="mt-3" onSubmit={handleSignSave}>
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label">Zertifikat (PEM)</label>
              <input type="file" className="form-control" accept=".pem,.crt,.cer,.txt" onChange={(e) => setSignCertFile(e.target.files?.[0] || null)} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Privater Schlüssel (unverschlüsselt)</label>
              <input type="file" className="form-control" accept=".pem,.key,.txt" onChange={(e) => setSignKeyFile(e.target.files?.[0] || null)} />
            </div>
            <div className="col-12">
              <label className="form-label">Oder PEM kombiniert einfügen</label>
              <textarea className="form-control font-monospace" rows={3} value={signCombined} onChange={(e) => setSignCombined(e.target.value)} />
            </div>
          </div>
          <div className="d-flex gap-2 mt-3">
            <button type="button" className="btn btn-outline-secondary" disabled={signBusy} onClick={handleSignPreview}>Prüfen</button>
            <button type="submit" className="btn btn-primary" disabled={signBusy}>{signBusy ? "Speichere…" : "Signaturzertifikat speichern"}</button>
          </div>
        </form>
        </details>
      </div>

      <div className="card mb-4 p-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h3 className="h5 mb-1">Betreuungsidentität (Apple Configurator)</h3>
            <p className="text-muted small mb-2">
              Organisation inkl. Supervision Identity als <code>.organization</code> für Apple Configurator
              (analog Jamf School). Das öffentliche Zertifikat geht zusätzlich als
              <code> supervising_host_certs</code> in DEP-Profile.
            </p>
          </div>
          <StatusBadge
            status={supervisionIdentity.status}
            label={supervisionIdentity.configured
              ? expiryLabel(supervisionIdentity.notAfter, supervisionIdentity.daysRemaining)
              : "keine Identität"}
          />
        </div>
        <div className="d-flex flex-wrap gap-3 small mb-3">
          {supervisionIdentity.subject && <span><strong>Subject:</strong> {supervisionIdentity.subject}</span>}
          {supervisionIdentity.issuer && <span><strong>Aussteller:</strong> {supervisionIdentity.issuer}</span>}
          {supervisionIdentity.notAfter && <span><strong>Gültig bis:</strong> {formatDateTime(supervisionIdentity.notAfter)}</span>}
          {supervisionIdentity.updatedAt && <span><strong>Zuletzt gesetzt:</strong> {formatDateTime(supervisionIdentity.updatedAt)}</span>}
          {configOrg.uuid && <span><strong>Org-UUID:</strong> <code>{configOrg.uuid}</code></span>}
        </div>
        <div className="alert alert-secondary small">
          Exportiert wird eine Plist mit Stammdaten und <code>identityReference</code> (passwortgeschütztes PKCS#12).
          In Configurator: Organisation importieren und dasselbe Passwort eingeben.
          Nach Erzeugen/Wechsel der Identität DEP-Profile speichern; bereits eingerichtete Geräte brauchen neues ADE-Enrollment.
        </div>

        <h4 className="h6 mt-3">Organisation (für .organization)</h4>
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <label className="form-label">Name</label>
            <input
              className="form-control"
              value={configOrg.name}
              onChange={(e) => setConfigOrgField("name", e.target.value)}
              disabled={supervisionBusy}
              placeholder="z. B. Xavier: MZ Jena"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Adresse</label>
            <input
              className="form-control"
              value={configOrg.address}
              onChange={(e) => setConfigOrgField("address", e.target.value)}
              disabled={supervisionBusy}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">E-Mail</label>
            <input
              type="email"
              className="form-control"
              value={configOrg.email}
              onChange={(e) => setConfigOrgField("email", e.target.value)}
              disabled={supervisionBusy}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Telefon</label>
            <input
              className="form-control"
              value={configOrg.phone}
              onChange={(e) => setConfigOrgField("phone", e.target.value)}
              disabled={supervisionBusy}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Apple-ID (Deploy)</label>
            <input
              className="form-control"
              value={configOrg.appleId}
              onChange={(e) => setConfigOrgField("appleId", e.target.value)}
              disabled={supervisionBusy}
              placeholder="apple-deploy-id@…"
            />
          </div>
          <div className="col-md-8">
            <label className="form-label">alternateDirectoryServicesID (ASM/ABM)</label>
            <input
              className="form-control"
              value={configOrg.alternateDirectoryServicesId}
              onChange={(e) => setConfigOrgField("alternateDirectoryServicesId", e.target.value)}
              disabled={supervisionBusy}
              placeholder="optional, z. B. 001469-07-…"
            />
          </div>
          <div className="col-md-4 d-flex align-items-end">
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              disabled={supervisionBusy}
              onClick={handleConfiguratorOrgSave}
            >
              Organisationsdaten speichern
            </button>
          </div>
        </div>

        <h4 className="h6">Betreuungszertifikat erzeugen</h4>
        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-4">
            <label className="form-label">Common Name</label>
            <input
              className="form-control"
              value={supervisionCn}
              onChange={(e) => setSupervisionCn(e.target.value)}
              disabled={supervisionBusy}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Organisation (Zertifikat)</label>
            <input
              className="form-control"
              value={supervisionOrg}
              onChange={(e) => setSupervisionOrg(e.target.value)}
              disabled={supervisionBusy}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Gültigkeit (Tage)</label>
            <input
              type="number"
              min={30}
              max={3650}
              className="form-control"
              value={supervisionDays}
              onChange={(e) => setSupervisionDays(Number(e.target.value))}
              disabled={supervisionBusy}
            />
          </div>
          <div className="col-md-2">
            <button
              type="button"
              className="btn btn-primary w-100"
              disabled={supervisionBusy}
              onClick={handleSupervisionGenerate}
            >
              {supervisionBusy ? "…" : "Erzeugen"}
            </button>
          </div>
        </div>
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label">Export-Passwort</label>
            <input
              type="password"
              className="form-control"
              autoComplete="new-password"
              value={supervisionP12Password}
              onChange={(e) => setSupervisionP12Password(e.target.value)}
              disabled={supervisionBusy || !supervisionIdentity.configured}
              placeholder="mindestens 4 Zeichen"
            />
          </div>
          <div className="col-md-4">
            <button
              type="button"
              className="btn btn-outline-primary w-100"
              disabled={supervisionBusy || !supervisionIdentity.configured || !configOrg.name.trim()}
              onClick={handleOrganizationFileDownload}
            >
              .organization laden
            </button>
          </div>
          <div className="col-md-4">
            <button
              type="button"
              className="btn btn-outline-secondary w-100"
              disabled={supervisionBusy || !supervisionIdentity.configured}
              onClick={handleSupervisionP12Download}
            >
              nur .p12 laden
            </button>
          </div>
        </div>
      </div>

      <div className="card mb-4 p-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h3 className="h5 mb-1">Inhalte-Token (Apps and Books / VPP)</h3>
            <p className="text-muted small mb-2">
              In Apple Business Manager oder Apple School Manager unter Apps und Bücher ein Inhalte-Token
              herunterladen (<code>.vpptoken</code>) und hier hochladen. Der Katalog erscheint auf der{" "}
              <Link to="/applications">Applications</Link>-Seite.
            </p>
          </div>
          <StatusBadge
            status={vpp.status}
            label={vpp.configured ? expiryLabel(vpp.tokenExpiry, vpp.daysRemaining) : "kein Token"}
          />
        </div>
        <div className="d-flex flex-wrap gap-3 small mb-3">
          {vpp.orgName && <span><strong>Organisation:</strong> {vpp.orgName}</span>}
          {vpp.locationName && <span><strong>Standort:</strong> {vpp.locationName}</span>}
          {vpp.countryCode && <span><strong>Land:</strong> {vpp.countryCode}</span>}
          {vpp.tokenExpiry && (
            <span><strong>Token gültig bis:</strong> {formatDateTime(vpp.tokenExpiry)}</span>
          )}
          {vpp.updatedAt && (
            <span><strong>Zuletzt importiert:</strong> {formatDateTime(vpp.updatedAt)}</span>
          )}
        </div>
        {vpp.message && !vpp.configured && (
          <div className="alert alert-secondary py-2 small">{vpp.message}</div>
        )}

        <form onSubmit={handleVppRenew}>
          <div className="row g-2">
            <div className="col-md-6">
              <label className="form-label">Inhalte-Token (.vpptoken)</label>
              <input
                type="file"
                className="form-control"
                accept=".vpptoken,.txt,.bin"
                onChange={(e) => setVppFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Oder Token-Text einfügen</label>
              <textarea
                className="form-control font-monospace"
                rows={3}
                placeholder="Inhalt der .vpptoken-Datei"
                value={vppText}
                onChange={(e) => setVppText(e.target.value)}
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary mt-3" disabled={vppBusy}>
            {vppBusy ? "Prüfe bei Apple…" : "Inhalte-Token importieren"}
          </button>
        </form>
      </div>

      <p className="small text-muted">
        Ablaufdaten werden auch in der{" "}
        <Link to="/device-registrations">Geräte-Registrierung</Link> angezeigt.
      </p>
    </div>
  );
};

export default Settings;
